import { defaultClient } from '@mint-sdk/api-core';
import { OmniTraffickingIntegrationResponseSchema } from '@mint-sdk/api-integrations/compat';
import z from 'zod';

import {
  OmniTraffickingAdformCreateRequestSchema,
  OmniTraffickingAdformGetAdvertisersQuerySchema,
  OmniTraffickingAdformGetAdvertisersQuerySchemaLegacy,
  OmniTraffickingAdformOAuthCallbackRequestSchema,
  OmniTraffickingAdformOAuthCallbackResponseSchema,
  OmniTraffickingAdformOAuthUrlRequestSchema,
  OmniTraffickingAdformOAuthUrlResponseSchema,
  OmniTraffickingAdformOAuthUrlResponseSchemaLegacy,
  OmniTraffickingAdformUpdateRequestSchema,
  OmniTraffickingAppsFlyerCreateRequestSchema,
  OmniTraffickingAppsFlyerUpdateRequestSchema,
  OmniTraffickingCMGetAdvertisersResponseSchema,
  OmniTraffickingCMGetProfilesResponseSchema,
  OmniTraffickingCMCreateRequestSchema,
  OmniTraffickingCMOauthCallbackQuerySchema,
  OmniTraffickingCMOauthCallbackResponseSchema,
  OmniTraffickingCMOauthUrlQuerySchemaLegacy,
  OmniTraffickingCMOauthUrlRequestSchema,
  OmniTraffickingCMOauthUrlResponseSchema,
  OmniTraffickingCMOauthUrlResponseSchemaLegacy,
  OmniTraffickingCMOGetAdvertisersQuerySchema,
  OmniTraffickingCMOGetAdvertisersQuerySchemaLegacy,
  OmniTraffickingCMOGetProfilesQuerySchema,
  OmniTraffickingCMOGetProfilesQuerySchemaLegacy,
  OmniTraffickingCMUpdateRequestSchema,
  OmniTraffickingDeleteResponseSchema,
  OmniTraffickingGetAdvertisers,
  OmniTraffickingIntegrationsQueryParams,
} from '../shared/schemas/integrations.js';

export const getIntegrationList = defaultClient.query({
  url: 'omni-trafficking/v1/integrations/' as const,
  method: 'GET',
  querySchema: OmniTraffickingIntegrationsQueryParams,
  responseSchema: OmniTraffickingIntegrationResponseSchema,
  snakeCaseQueryParams: true,
  processResponse(data) {
    // TODO: remove hack when BE is ready
    if (data?.data?.items) {
      for (const item of data.data.items) {
        item.usingInAccounts = false;
        item.expired = !item.isActive;
      }
    }
    return data?.data?.items;
  },
});

export const getIntegrationIsVerified = defaultClient.query({
  url: 'omni-trafficking/v1/integrations/$integrationId/' as const,
  method: 'GET',
  querySchema: z.object({}),
  responseSchema: OmniTraffickingIntegrationResponseSchema,
  snakeCaseQueryParams: true,
  processResponse(data) {
    return data?.data?.items?.[0]?.isActive;
  },
});

// TODO(adformBackendOwnedAuth): delete along with the flag, together with
// getAdformOAuthUrlLegacy, getAdFormAdvertisersLegacy, createAdformIntegration
// (no "Legacy" suffix!) and the legacy client-owned Adform flow that calls
// them.
export const postAdformOAuthCallback = defaultClient.mutation({
  url: 'omni-trafficking/v1/integrations/adform/oauth-callback/' as const,
  method: 'POST',
  requestSchema: OmniTraffickingAdformOAuthCallbackRequestSchema,
  responseSchema: OmniTraffickingAdformOAuthCallbackResponseSchema,
  snakeCaseQueryParams: true,
  processResponse(data) {
    return data?.data?.items[0]?.oauthFlowId;
  },
});

// TODO(adformBackendOwnedAuth): delete along with the flag.
export const getAdformOAuthUrlLegacy = defaultClient.query({
  url: 'omni-trafficking/v1/integrations/adform/oauth-url/' as const,
  method: 'GET',
  querySchema: z.object({}),
  snakeCaseQueryParams: true,
  responseSchema: OmniTraffickingAdformOAuthUrlResponseSchemaLegacy,
  processResponse(data) {
    return data?.data?.items?.[0];
  },
});

export const getAdformOAuthUrl = defaultClient.mutation({
  url: 'omni-trafficking/v1/integrations/adform/oauth-url/' as const,
  method: 'POST',
  requestSchema: OmniTraffickingAdformOAuthUrlRequestSchema,
  responseSchema: OmniTraffickingAdformOAuthUrlResponseSchema,
  snakeCaseQueryParams: true,
  processResponse(data) {
    return data?.data?.items?.[0];
  },
});

export const deleteIntegration = defaultClient.mutation({
  url: 'omni-trafficking/v1/integrations/$integrationId/' as const,
  method: 'DELETE',
  responseSchema: OmniTraffickingDeleteResponseSchema,
  processResponse(data) {
    return data?.data?.items?.[0];
  },
});

export const updateAdFormIntegration = defaultClient.mutation({
  url: 'omni-trafficking/v1/integrations/adform/$integrationId/' as const,
  method: 'PATCH',
  requestSchema: OmniTraffickingAdformUpdateRequestSchema,
  responseSchema: OmniTraffickingIntegrationResponseSchema,
  processResponse(data) {
    return data?.data?.items?.[0];
  },
});

export const getAdFormAdvertisers = defaultClient.query({
  url: 'omni-trafficking/v1/external-data/adform/advertisers/' as const,
  method: 'GET',
  querySchema: OmniTraffickingAdformGetAdvertisersQuerySchema,
  responseSchema: OmniTraffickingGetAdvertisers,
  snakeCaseQueryParams: true,
  processResponse(data) {
    return data?.data?.items;
  },
});

// TODO(adformBackendOwnedAuth): delete along with the flag.
export const getAdFormAdvertisersLegacy = defaultClient.query({
  url: 'omni-trafficking/v1/external-data/adform/advertisers/' as const,
  method: 'GET',
  querySchema: OmniTraffickingAdformGetAdvertisersQuerySchemaLegacy,
  responseSchema: OmniTraffickingGetAdvertisers,
  snakeCaseQueryParams: true,
  processResponse(data) {
    return data?.data?.items;
  },
});

// TODO(adformBackendOwnedAuth): delete along with the flag. No "Legacy"
// suffix here - don't miss it during cleanup.
export const createAdformIntegration = defaultClient.mutation({
  url: 'omni-trafficking/v1/integrations/adform/' as const,
  method: 'POST',
  requestSchema: OmniTraffickingAdformCreateRequestSchema,
  responseSchema: OmniTraffickingIntegrationResponseSchema,
  processResponse(data) {
    return data?.data?.items?.[0];
  },
});

export const createAppsFlyerIntegration = defaultClient.mutation({
  url: 'omni-trafficking/v1/integrations/appsflyer/' as const,
  method: 'POST',
  requestSchema: OmniTraffickingAppsFlyerCreateRequestSchema,
  responseSchema: OmniTraffickingIntegrationResponseSchema,
  processResponse(data) {
    return data?.data?.items?.[0];
  },
});

export const updateAppsFlyerIntegration = defaultClient.mutation({
  url: 'omni-trafficking/v1/integrations/appsflyer/$integrationId/' as const,
  method: 'PATCH',
  requestSchema: OmniTraffickingAppsFlyerUpdateRequestSchema,
  responseSchema: OmniTraffickingIntegrationResponseSchema,
  processResponse(data) {
    return data?.data?.items?.[0];
  },
});

// TODO(cm360BackendOwnedAuth): delete along with the flag, together with
// getCMIntegrationOAuthUrlLegacy, createCMIntegration (no "Legacy" suffix!),
// getCMAdvertisersLegacy and getCMProfilesLegacy, and the legacy
// client-owned CM360 flow that calls them.
export const postCMIntegrationOAuthCallback = defaultClient.mutation({
  url: 'omni-trafficking/v1/integrations/campaign-manager/oauth-callback/' as const,
  method: 'POST',
  requestSchema: z.object({}),
  snakeCaseQueryParams: true,
  querySchema: OmniTraffickingCMOauthCallbackQuerySchema,
  responseSchema: OmniTraffickingCMOauthCallbackResponseSchema,
  processResponse(data) {
    return data?.data?.items?.[0];
  },
});

// TODO(cm360BackendOwnedAuth): delete along with the flag.
export const getCMIntegrationOAuthUrlLegacy = defaultClient.query({
  url: 'omni-trafficking/v1/integrations/campaign-manager/oauth-url/' as const,
  method: 'GET',
  querySchema: OmniTraffickingCMOauthUrlQuerySchemaLegacy,
  responseSchema: OmniTraffickingCMOauthUrlResponseSchemaLegacy,
  clientOptions: {
    request: {
      snakeCaseQueryParams: true,
    },
  },
  processResponse(data) {
    return data?.data?.items?.[0];
  },
});

export const getCMIntegrationOAuthUrl = defaultClient.mutation({
  url: 'omni-trafficking/v1/integrations/campaign-manager/oauth-url/' as const,
  method: 'POST',
  requestSchema: OmniTraffickingCMOauthUrlRequestSchema,
  responseSchema: OmniTraffickingCMOauthUrlResponseSchema,
  snakeCaseQueryParams: true,
  processResponse(data) {
    return data?.data?.items?.[0];
  },
});

// TODO(cm360BackendOwnedAuth): delete along with the flag. No "Legacy"
// suffix here - don't miss it during cleanup.
export const createCMIntegration = defaultClient.mutation({
  url: 'omni-trafficking/v1/integrations/campaign-manager/' as const,
  method: 'POST',
  requestSchema: OmniTraffickingCMCreateRequestSchema,
  responseSchema: OmniTraffickingIntegrationResponseSchema,
  processResponse(data) {
    return data?.data?.items?.[0];
  },
});

export const getCMAdvertisers = defaultClient.query({
  url: 'omni-trafficking/v1/external-data/campaign-manager/advertisers/' as const,
  method: 'GET',
  querySchema: OmniTraffickingCMOGetAdvertisersQuerySchema,
  responseSchema: OmniTraffickingCMGetAdvertisersResponseSchema,
  snakeCaseQueryParams: true,
  processResponse(data) {
    return data?.data?.items;
  },
});

export const getCMProfiles = defaultClient.query({
  url: 'omni-trafficking/v1/external-data/campaign-manager/profiles/' as const,
  method: 'GET',
  querySchema: OmniTraffickingCMOGetProfilesQuerySchema,
  responseSchema: OmniTraffickingCMGetProfilesResponseSchema,
  snakeCaseQueryParams: true,
  processResponse(data) {
    return data?.data?.items;
  },
});

// TODO(cm360BackendOwnedAuth): delete this and getCMProfilesLegacy below
// along with the flag.
export const getCMAdvertisersLegacy = defaultClient.query({
  url: 'omni-trafficking/v1/external-data/campaign-manager/advertisers/' as const,
  method: 'GET',
  querySchema: OmniTraffickingCMOGetAdvertisersQuerySchemaLegacy,
  responseSchema: OmniTraffickingCMGetAdvertisersResponseSchema,
  snakeCaseQueryParams: true,
  processResponse(data) {
    return data?.data?.items;
  },
});

export const getCMProfilesLegacy = defaultClient.query({
  url: 'omni-trafficking/v1/external-data/campaign-manager/profiles/' as const,
  method: 'GET',
  querySchema: OmniTraffickingCMOGetProfilesQuerySchemaLegacy,
  responseSchema: OmniTraffickingCMGetProfilesResponseSchema,
  snakeCaseQueryParams: true,
  processResponse(data) {
    return data?.data?.items;
  },
});

export const updateCMIntegration = defaultClient.mutation({
  url: 'omni-trafficking/v1/integrations/campaign-manager/$integrationId/' as const,
  method: 'PATCH',
  requestSchema: OmniTraffickingCMUpdateRequestSchema,
  responseSchema: OmniTraffickingIntegrationResponseSchema,
  processResponse(data) {
    return data?.data?.items?.[0];
  },
});
